/**
 * Архивация доски и восстановление из архива (§8.1, §9 «Опасная зона»).
 *
 * С доской в архив уходят все её активные задачи, а при восстановлении
 * возвращаются только они: задачи, заархивированные раньше по отдельности,
 * остаются в архиве. Об этом диалоги говорят прямо — последствия
 * затрагивают чужую работу, и узнавать о них после нажатия нельзя.
 */

import { Permission } from "@taskflow/contracts";
import { ArchiveX, Undo2 } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { usePermission, type WsError } from "@taskflow/client-core";
import { Button, ConfirmDialog, EmptyState, toast } from "@/shared/ui";
import { Archive } from "@/shared/ui/motionIcons";

import {
  type BoardSummary,
  useArchiveBoardMutation,
  useRestoreBoardMutation,
} from "@taskflow/client-core/boards";
import { formatArchivedAt, tasksCount } from "./archiveFormat";

/** «Опасная зона» в общих настройках: только с правом board.delete. */
export function ArchiveBoardSection({ board }: { board: BoardSummary }) {
  const canArchive = usePermission(Permission.BOARD_DELETE, board.permissions);
  const [archive, { isLoading }] = useArchiveBoardMutation();
  const [confirming, setConfirming] = useState(false);
  const navigate = useNavigate();

  if (!canArchive) return null;

  const confirm = async () => {
    try {
      const result = await archive({ boardId: board.id, version: board.version }).unwrap();
      toast.success(
        `Доска «${board.name}» перенесена в архив`,
        result.archivedTasks === 0
          ? "Активных задач на ней не было"
          : `Вместе с ней в архив перенесены: ${tasksCount(result.archivedTasks)}`,
      );
      setConfirming(false);
      // Доски больше нет в рабочих списках — открывается другая доска подразделения.
      void navigate(`/tasks?department=${board.departmentCode}`);
    } catch (raw) {
      toast.error(raw as WsError, "Не удалось перенести доску в архив");
    }
  };

  return (
    <section className="flex flex-col gap-2.5 border-t border-line pt-5">
      <h2 className="text-[13px] font-extrabold text-danger">Опасная зона</h2>
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-danger px-4 py-3">
        <div className="min-w-0">
          <p className="text-[13px] font-semibold text-fg">Перенести доску в архив</p>
          <p className="text-[12px] text-muted">
            Доска исчезнет из списка досок, её задачи — с канбана и из активных фильтров.
            Данные и история сохранятся; доску можно восстановить из «Архива досок».
          </p>
        </div>
        <Button variant="danger" size="sm" icon={Archive} onClick={() => setConfirming(true)}>
          В архив
        </Button>
      </div>

      <ConfirmDialog
        open={confirming}
        tone="danger"
        title="Перенести доску в архив?"
        description={`Доска «${board.name}» исчезнет из списка досок у всех участников.`}
        warning={
          board.taskCount === 0
            ? "Активных задач на доске нет."
            : `Вместе с доской в архив перейдут все её активные задачи: ${tasksCount(board.taskCount)}. При восстановлении доски они вернутся в свои столбцы.`
        }
        confirmLabel="В архив"
        loading={isLoading}
        onConfirm={() => void confirm()}
        onCancel={() => setConfirming(false)}
      />
    </section>
  );
}

/** Подтверждение восстановления доски вместе с её задачами. */
export function RestoreBoardDialog({
  board,
  onClose,
}: {
  board: BoardSummary & { archivedTaskCount?: number };
  onClose: () => void;
}) {
  const [restore, { isLoading }] = useRestoreBoardMutation();
  const navigate = useNavigate();

  const confirm = async () => {
    try {
      const result = await restore({ boardId: board.id, version: board.version }).unwrap();
      toast.success(
        `Доска «${board.name}» восстановлена`,
        result.restoredTasks === 0
          ? undefined
          : `Возвращены в свои столбцы: ${tasksCount(result.restoredTasks)}`,
      );
      onClose();
      void navigate(`/boards/${board.id}`);
    } catch (raw) {
      toast.error(raw as WsError, "Не удалось восстановить доску");
    }
  };

  const count = board.archivedTaskCount;
  return (
    <ConfirmDialog
      open
      title="Восстановить доску?"
      description={`Доска «${board.name}» вернётся в список досок подразделения.`}
      warning={
        count === undefined || count === 0
          ? "Задачи, заархивированные до архивации доски, останутся в архиве."
          : `Вместе с доской вернутся задачи, ушедшие в архив с ней: ${tasksCount(count)}. Задачи, заархивированные раньше по отдельности, останутся в архиве.`
      }
      confirmLabel="Восстановить"
      loading={isLoading}
      onConfirm={() => void confirm()}
      onCancel={onClose}
    />
  );
}

/**
 * Архивная доска, открытая по ссылке или закладке. Канбан пуст — задачи
 * ушли в архив вместе с ней, — и вместо пустых столбцов пользователь
 * видит, что произошло и как вернуть доску.
 */
export function ArchivedBoardNotice({ board }: { board: BoardSummary }) {
  const canRestore = usePermission(Permission.BOARD_DELETE, board.permissions);
  const [restoring, setRestoring] = useState(false);

  return (
    <>
      <EmptyState
        icon={ArchiveX}
        title={`Доска «${board.name}» в архиве`}
        description={`${
          board.archivedAt === null ? "Доска" : `Перенесена в архив ${formatArchivedAt(board.archivedAt)}.`
        } Её задачи находятся в архиве задач доски.`}
        action={
          <div className="flex flex-wrap justify-center gap-2">
            {canRestore && (
              <Button icon={Undo2} onClick={() => setRestoring(true)}>
                Восстановить доску
              </Button>
            )}
            <Link to={`/boards/${board.id}/archive`}>
              <Button variant="secondary">Архив задач</Button>
            </Link>
            <Link to="/boards/archived">
              <Button variant="secondary">Архив досок</Button>
            </Link>
          </div>
        }
      />
      {restoring && <RestoreBoardDialog board={board} onClose={() => setRestoring(false)} />}
    </>
  );
}
